/* @img-migrated */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, Unlock, Search, X, Plus, Eye, Loader2,
  Crown, Star, Sparkles, Zap, Shield, UserPlus, UserMinus, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  hybrid:    { label: 'Hybrid',    color: '#3B82F6', icon: Zap,       description: 'Hybrid digital members' },
  essential: { label: 'Essential', color: '#64748B', icon: Sparkles,  description: 'Foundational tier' },
  premier:   { label: 'Premier',   color: '#D4AF37', icon: Star,      description: 'Premium business tier' },
  executive: { label: 'Executive', color: '#0F172A', icon: Crown,     description: 'Top-tier / leadership' },
};

const TABS = [
  { id: 'tier',     label: 'Tier Access',         icon: Shield },
  { id: 'override', label: 'Individual Overrides', icon: UserPlus },
  { id: 'preview',  label: 'Preview Mode',         icon: Eye },
];

const MemberDealsAccessPage = () => {
  const [tab, setTab] = useState('tier');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(null);
  const [previewTier, setPreviewTier] = useState('hybrid');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/member-deals/access`, { withCredentials: true });
      setConfig(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load access config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const toggleTier = async (tier) => {
    if (!config) return;
    const next = { ...config.tier_access, [tier]: !config.tier_access[tier] };
    setSavingTier(tier);
    setConfig({ ...config, tier_access: next }); // optimistic
    try {
      await axios.put(`${API}/admin/member-deals/access`, { tier_access: next }, { withCredentials: true });
      toast.success(`${TIER_META[tier].label} ${next[tier] ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Save failed — reverting');
      fetchConfig();
    } finally {
      setSavingTier(null);
    }
  };

  // ----- Overrides -----
  const runSearch = async (query) => {
    setSearching(true);
    try {
      const res = await axios.get(`${API}/admin/member-deals/access/search-members`, {
        params: { q: query }, withCredentials: true,
      });
      setSearchResults(res.data?.results || []);
    } catch { /* ignore */ }
    finally { setSearching(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => runSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const setOverride = async (user, action) => {
    setOverrideBusy(user.id);
    try {
      await axios.post(`${API}/admin/member-deals/overrides`, { user_id: user.id, action }, { withCredentials: true });
      toast.success(action === 'clear'
        ? `Removed override for ${user.name || user.email}`
        : `${action === 'grant' ? 'Granted' : 'Denied'} ${user.name || user.email}`);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setOverrideBusy(null);
    }
  };

  // ----- Preview -----
  const runPreview = async (tier) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/portal/member-benefits/access`, {
        params: { preview_tier: tier }, withCredentials: true,
      });
      setPreviewResult(res.data);
    } catch (err) {
      toast.error('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'preview') runPreview(previewTier);
     
  }, [tab, previewTier]);

  const overrideIds = useMemo(() => {
    if (!config) return new Set();
    return new Set([
      ...(config.overrides.grant || []).map((u) => u.id),
      ...(config.overrides.deny || []).map((u) => u.id),
    ]);
  }, [config]);

  const overrideStateOf = (id) => {
    if (!config) return null;
    if ((config.overrides.grant || []).some((u) => u.id === id)) return 'grant';
    if ((config.overrides.deny || []).some((u) => u.id === id)) return 'deny';
    return null;
  };

  if (loading || !config) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid="member-deals-access-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#002249] tracking-tight">Member Deals Access</h1>
              <p className="text-[13px] text-slate-500 mt-0.5">Control which membership tiers can access Member Deals — plus per-member overrides.</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex border-b border-slate-200">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex-1 px-4 py-3 inline-flex items-center justify-center gap-2 text-[13px] font-semibold border-b-2 transition-colors ${
                    active
                      ? 'border-[#E63946] text-[#002249] bg-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`mda-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />{t.label}
                </button>
              );
            })}
          </div>

          {/* Tier Access */}
          {tab === 'tier' && (
            <div className="p-5 sm:p-7" data-testid="mda-tier-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {Object.entries(TIER_META).map(([tier, meta]) => {
                  const Icon = meta.icon;
                  const enabled = !!config.tier_access[tier];
                  return (
                    <div
                      key={tier}
                      className={`relative border-2 rounded-xl p-5 transition-all ${
                        enabled ? 'bg-emerald-50/40 border-emerald-300' : 'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`mda-tier-card-${tier}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-11 h-11 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                          style={{ background: meta.color }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14.5px] font-bold text-[#002249]">{meta.label}</p>
                          <p className="text-[11.5px] text-slate-500 mt-0.5">{meta.description}</p>
                          <p className={`mt-3 text-[11px] font-semibold inline-flex items-center gap-1 ${enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {enabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                            {enabled ? 'Has access' : 'Blocked'}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleTier(tier)}
                          disabled={savingTier === tier}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                          data-testid={`mda-tier-toggle-${tier}`}
                          aria-label={`Toggle ${meta.label}`}
                        >
                          <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[12px] text-amber-800 leading-relaxed">
                  Members on disabled tiers will see a premium <strong>Access Required</strong> screen
                  with an upgrade CTA. Use Individual Overrides to grant or deny specific accounts regardless of tier.
                </p>
              </div>
            </div>
          )}

          {/* Overrides */}
          {tab === 'override' && (
            <div className="p-5 sm:p-7" data-testid="mda-override-panel">
              {/* Active overrides */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <OverrideList
                  title="Granted access"
                  variant="grant"
                  users={config.overrides.grant || []}
                  onClear={(u) => setOverride(u, 'clear')}
                  busyId={overrideBusy}
                />
                <OverrideList
                  title="Denied access"
                  variant="deny"
                  users={config.overrides.deny || []}
                  onClear={(u) => setOverride(u, 'clear')}
                  busyId={overrideBusy}
                />
              </div>

              {/* Search + add */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-[12.5px] font-bold text-[#002249] mb-2">Add an individual override</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search members by name or email…"
                    className="pl-9 h-10 text-[13px] bg-white"
                    data-testid="mda-override-search"
                  />
                </div>
                {searching && <p className="mt-2 text-[11px] text-slate-400">Searching…</p>}
                <ul className="mt-3 space-y-1.5 max-h-[300px] overflow-y-auto">
                  {searchResults.map((u) => {
                    const state = overrideStateOf(u.id);
                    return (
                      <li
                        key={u.id}
                        className="flex items-center gap-3 px-3 py-2 bg-white border border-slate-200 rounded-lg"
                        data-testid={`mda-search-result-${u.id}`}
                      >
                        {u.picture ? (
                          <Img variant="avatar" src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#002249] text-white flex items-center justify-center text-[11px] font-bold">
                            {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-[#002249] truncate">{u.name || u.email}</p>
                          <p className="text-[11px] text-slate-500 truncate">
                            {u.email}{u.membership_tier ? ` · ${u.membership_tier}` : ''}
                          </p>
                        </div>
                        {state === 'grant' && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Granted</span>
                        )}
                        {state === 'deny' && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-rose-700">Denied</span>
                        )}
                        <Button
                          onClick={() => setOverride(u, 'grant')}
                          disabled={overrideBusy === u.id}
                          variant="outline"
                          size="sm"
                          className="h-8 px-2.5 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`mda-grant-${u.id}`}
                        >
                          <UserPlus className="w-3 h-3" />
                        </Button>
                        <Button
                          onClick={() => setOverride(u, 'deny')}
                          disabled={overrideBusy === u.id}
                          variant="outline"
                          size="sm"
                          className="h-8 px-2.5 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`mda-deny-${u.id}`}
                        >
                          <UserMinus className="w-3 h-3" />
                        </Button>
                      </li>
                    );
                  })}
                  {!searching && searchResults.length === 0 && search && (
                    <li className="text-[12px] text-slate-400 px-3 py-2">No members match "{search}"</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* Preview */}
          {tab === 'preview' && (
            <div className="p-5 sm:p-7" data-testid="mda-preview-panel">
              <p className="text-[13px] text-slate-600 mb-4">
                Test the gate. The decision below shows what a member on the chosen tier would see when they open Member Deals.
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(TIER_META).map(([tier, meta]) => {
                  const Icon = meta.icon;
                  const active = previewTier === tier;
                  return (
                    <button
                      key={tier}
                      onClick={() => setPreviewTier(tier)}
                      className={`inline-flex items-center gap-2 px-3.5 h-10 rounded-lg border text-[12.5px] font-semibold transition-all ${
                        active ? 'bg-[#002249] border-[#002249] text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`mda-preview-tier-${tier}`}
                    >
                      <Icon className="w-4 h-4" style={!active ? { color: meta.color } : {}} />
                      {meta.label}
                    </button>
                  );
                })}
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 bg-slate-50">
                {previewLoading ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : previewResult ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {previewResult.allowed ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11.5px] font-bold uppercase tracking-wide">
                          <CheckCircle2 className="w-3.5 h-3.5" />Access granted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11.5px] font-bold uppercase tracking-wide">
                          <Lock className="w-3.5 h-3.5" />Access blocked
                        </span>
                      )}
                      <span className="text-[11.5px] text-slate-500">Reason: {previewResult.reason}</span>
                    </div>
                    {previewResult.allowed ? (
                      <p className="text-[13px] text-slate-600">A <strong>{TIER_META[previewTier].label}</strong> member would see the full directory of member discounts.</p>
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <p className="text-[12px] uppercase tracking-wider text-[#E63946] font-bold">Member Deals Access Required</p>
                        <h4 className="text-[16px] font-bold text-[#002249] mt-1">This feature is available for selected membership tiers.</h4>
                        <p className="text-[12.5px] text-slate-500 mt-1">Upgrade your membership or contact Membership Relations.</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OverrideList = ({ title, variant, users, onClear, busyId }) => {
  const colorClasses = variant === 'grant'
    ? 'border-emerald-200 bg-emerald-50'
    : 'border-rose-200 bg-rose-50';
  const headerColor = variant === 'grant' ? 'text-emerald-700' : 'text-rose-700';
  return (
    <div className={`border ${colorClasses} rounded-xl p-4`} data-testid={`mda-override-${variant}-list`}>
      <p className={`text-[11px] font-bold uppercase tracking-[0.12em] ${headerColor}`}>{title}</p>
      <p className="text-[11.5px] text-slate-500 mt-0.5 mb-3">{users.length} member{users.length !== 1 ? 's' : ''}</p>
      {users.length === 0 ? (
        <p className="text-[11.5px] text-slate-400">None set.</p>
      ) : (
        <ul className="space-y-1.5">
          {users.map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5"
              data-testid={`mda-override-${variant}-${u.id}`}
            >
              <div className="w-7 h-7 rounded-full bg-[#002249] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-bold text-[#002249] truncate">{u.name || u.email}</p>
                <p className="text-[10.5px] text-slate-500 truncate">{u.email}</p>
              </div>
              <button
                onClick={() => onClear(u)}
                disabled={busyId === u.id}
                className="text-slate-400 hover:text-rose-600 p-1 rounded-md"
                title="Remove override"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MemberDealsAccessPage;
